import { deactivateOrRemoveOrganizerPage, unfollowPage } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { InkindPageCard } from '@gik/inkind-page/components/InkindPageCard/InkindPageCard';
import { InkindPageCardBase } from '@gik/inkind-page/components/InkindPageCard/InkindPageCardBase';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { showPageDeactivatedModal } from '@gik/inkind-page/utils/modals';
import type { InkindAlgoliaObject } from '@gik/search/components';
import { Button } from '@gik/ui-legacy/Button';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { Text } from '@gik/ui-legacy/Text/Text';
import { Tooltip } from '@gik/ui-legacy/Tooltip';
import { UI } from '@gik/ui-legacy/UIManager';
import { useUserPages, useUserPendingPages } from '@gik/user-profile/hooks/useUserPages';
import type { Hit } from '@algolia/client-search';
import PlusIcon from '@heroicons/react/outline/PlusIcon';
import i18n from 'i18next';
import React from 'react';
import { useUserWallets } from '../../hooks/useUserWallets';
import UserPagesListSkeleton from './UserPagesListSkeleton';

export interface IUserPagesListProps {
  items?: InkindPageAPIModel[];
  onChange?(userPages: InkindPageAPIModel[] | Hit<InkindAlgoliaObject>[]): void;
}

const blockName = 'user-pages-list';

function UserPagesListComp({ items, onChange }: IUserPagesListProps) {
  const bem = bemBlock(blockName);

  const userId = useUserStore(state => state.id);
  const { data: userPages, mutate } = useUserPages();
  const { data: userPendingPages } = useUserPendingPages();

  const { data: wallets, error } = useUserWallets(userId);

  React.useEffect(() => {
    if (userPages) onChange?.(userPages);
  }, [userPages, onChange]);

  const isEmpty = userPages?.length === 0;

  const handleRemove = React.useCallback(
    async (
      inkindPageOrAlgolia: InkindPageAPIModel | Hit<InkindAlgoliaObject>,
      isOwner: boolean,
      isOrganizer: boolean
    ) => {
      if (isOrganizer) return;

      const inkindPage = inkindPageOrAlgolia as InkindPageAPIModel;
      const inkindAlgolia = inkindPageOrAlgolia as Hit<InkindAlgoliaObject>;

      const routeId = inkindPage.routeId ?? inkindAlgolia.objectID;

      const walletDeactivateCopy = i18n.t(
        wallets?.length && wallets.find(wallet => wallet.customerId === routeId)
          ? translationKeys.deactivatePageConfirmationText
          : translationKeys.deactivatePageConfirmationTextNoWallets
      );

      const response = await UI.confirm(
        isOwner ? <Text html>{walletDeactivateCopy}</Text> : 'Are you sure you want to stop following this page?',
        {
          okButtonProps: {
            variant: 'danger',
          },
        }
      );

      // stop if the user cancelled the confirmation dialog
      if (!response) return;

      const apiFunction = async () => {
        if (isOrganizer) return await Promise.resolve();
        if (isOwner) return await deactivateOrRemoveOrganizerPage(userId, routeId);
        return await unfollowPage(userId, routeId);
      };

      const newData = userPages.filter(page => page.objectID !== routeId);
      optimisticUpdate(newData, null, mutate, apiFunction);

      if (isOwner) {
        await showPageDeactivatedModal();
      }
    },
    [mutate, userId, userPages, wallets]
  );

  if (!userPages) {
    return <UserPagesListSkeleton />;
  }

  const isLoading = !userPages;

  if (isLoading) {
    return <UserPagesListSkeleton />;
  }

  return (
    <section className={`${bem('list-container')} ${isEmpty ? bem('list-container', 'empty') : ''}`}>
      <div className={bem('list')}>
        {userPendingPages?.map(page => (
          <InkindPageCard pending inkindPage={page} key={page.routeId} />
        ))}
        {userPages?.map(page => (
          <InkindPageCard onRemove={handleRemove} inkindPage={page} key={page.objectID} />
        ))}
        <CreateButtonCard />
      </div>
    </section>
  );
}

function CreateButtonCard() {
  const bem = bemBlock('create-button-card');

  return (
    <div className={bem()}>
      <EmptyInkindPageShape className={bem('empty-placeholder-card')} />
      <div className={bem('button-container')}>
        <Tooltip text="Add page">
          <Button type="a" href="/create" variant="secondary" circle size="xl">
            <SvgIcon Icon={PlusIcon} />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function EmptyInkindPageShape({ className }: UIComponent) {
  return <InkindPageCardBase className={className} admin={false} title={''} imageUrl={null} />;
}

export const UserPagesList = withComponentErrorBoundary(withSuspense(UserPagesListComp, UserPagesListSkeleton));
